import { DEFAULT_GERRIT_API_PREFIX, GERRIT_WEBHOOK_EVENTS } from './consts';

const WEBHOOK_REMOTE_PATH =
  '/config/server/webhooks~projects/All-Projects/remotes/qodo';
const WEBHOOK_REMOTES_PATH =
  '/config/server/webhooks~projects/All-Projects/remotes';

/**
 * The copyable curl that registers the Qodo webhook on Gerrit's All-Projects
 * (global) config via the webhooks plugin. Body mirrors the gateway's register
 * call (camelCase string values). Username is pre-filled; the password stays a
 * literal placeholder — the portal never holds the Gerrit password.
 */
export const buildCreateWebhookCommand = (
  gerritHost: string,
  webhookUrl: string,
  username: string,
  apiPrefix: string = DEFAULT_GERRIT_API_PREFIX,
): string => {
  const base = `${gerritHost.replace(/\/+$/, '')}${apiPrefix}`;
  const body = JSON.stringify({
    url: webhookUrl,
    events: [...GERRIT_WEBHOOK_EVENTS],
    maxTries: '3',
    sslVerify: 'true',
  });

  return [
    'curl --location --request PUT \\',
    `  '${base}${WEBHOOK_REMOTE_PATH}' \\`,
    "  --header 'Content-Type: application/json; charset=UTF-8' \\",
    `  --user '${username}:YOUR_HTTP_PASSWORD' \\`,
    `  --data-raw '${body}'`,
  ].join('\n');
};

/** The copyable curl that lists All-Projects webhook remotes, to verify creation. */
export const buildValidateWebhookCommand = (
  gerritHost: string,
  username: string,
  apiPrefix: string = DEFAULT_GERRIT_API_PREFIX,
): string => {
  const base = `${gerritHost.replace(/\/+$/, '')}${apiPrefix}`;

  return `curl --user '${username}:YOUR_HTTP_PASSWORD' '${base}${WEBHOOK_REMOTES_PATH}'`;
};

/**
 * Normalizes a user-entered Gerrit API prefix. Blank means "use the backend
 * default", so it returns undefined rather than an empty override. Requires a
 * bare absolute path — a full URL or a query string would compose a broken API
 * URL on every call. The backend re-validates; this is fast inline feedback.
 */
export const normalizeGerritApiPrefix = (
  value: string | undefined,
): string | undefined => {
  const trimmed = value?.trim();

  if (!trimmed) return undefined;

  if (!trimmed.startsWith('/')) return undefined;

  if (/:\/\/|\?|#/.test(trimmed)) return undefined;

  return trimmed.replace(/\/+$/, '') || '/';
};

/**
 * Normalizes a user-entered Gerrit base URL. Accepts a bare host (defaults to
 * https) or a full URL, requires an http/https scheme, and strips any trailing
 * slash so the stored base is canonical. Returns undefined when it isn't a
 * usable http(s) URL. The backend re-validates; this is fast inline feedback.
 */
export const normalizeGerritBaseUrl = (value: string): string | undefined => {
  const trimmed = value.trim();

  if (!trimmed) return undefined;

  const candidate = /:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const url = new URL(candidate);

    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return undefined;
    }

    return candidate.replace(/\/+$/, '');
  } catch {
    return undefined;
  }
};
